import { parseCard, type CardId } from '../../core/cards.js';

export type TeenPattiCategory = 'TRAIL' | 'PURE_SEQUENCE' | 'SEQUENCE' | 'COLOR' | 'PAIR' | 'HIGH_CARD';

const CATEGORY_VALUE: Record<TeenPattiCategory, number> = {
  TRAIL: 6,
  PURE_SEQUENCE: 5,
  SEQUENCE: 4,
  COLOR: 3,
  PAIR: 2,
  HIGH_CARD: 1,
};

export interface TeenPattiHandValue {
  category: TeenPattiCategory;
  /** Totally ordered strength; higher wins (before variant inversion). */
  value: number;
}

export interface TeenPattiRankOptions {
  /** 'second': A-2-3 ranks just below A-K-Q (most common). 'low': lowest sequence. */
  ace23: 'second' | 'low';
  /** Flush ranking: a colour (flush) beats a plain sequence, as in three-card poker. */
  colorFirst?: boolean;
  /** Three Card Brag ranking: 3-3-3 is the top prial and A-2-3 is the top run. */
  brag?: boolean;
}

const pack = (cat: number, a: number, b = 0, c = 0) => cat * 15 ** 3 + a * 15 ** 2 + b * 15 + c;

export function evaluateTeenPatti(cards: readonly CardId[], opts: TeenPattiRankOptions = { ace23: 'second' }): TeenPattiHandValue {
  if (cards.length !== 3) throw new Error('Teen Patti hands have exactly 3 cards');
  const parsed = cards.map(parseCard);
  const ranks = parsed.map((c) => c.rank).sort((a, b) => b - a) as [number, number, number];
  const flush = parsed.every((c) => c.suit === parsed[0]!.suit);
  const [r0, r1, r2] = ranks;

  if (r0 === r1 && r1 === r2) return { category: 'TRAIL', value: pack(CATEGORY_VALUE.TRAIL, 0, 0, opts.brag && r0 === 3 ? 15 : r0) };

  // Sequence detection, including A-2-3.
  let seqHigh: number | null = null;
  if (r0 - 1 === r1 && r1 - 1 === r2) seqHigh = r0;
  // A-K-Q = 14 (top). A-2-3: either 13.5 (second) or 3 (lowest). Use doubled scale to keep integers.
  let seqValue: number | null = seqHigh === null ? null : seqHigh * 2;
  if (r0 === 14 && r1 === 3 && r2 === 2) seqValue = opts.brag ? 29 : opts.ace23 === 'second' ? 27 : 6;
  if (seqValue !== null) {
    const cat = flush ? CATEGORY_VALUE.PURE_SEQUENCE : opts.colorFirst ? CATEGORY_VALUE.COLOR : CATEGORY_VALUE.SEQUENCE;
    return { category: flush ? 'PURE_SEQUENCE' : 'SEQUENCE', value: pack(cat, 0, 0, seqValue) };
  }
  if (flush) return { category: 'COLOR', value: pack(opts.colorFirst ? CATEGORY_VALUE.SEQUENCE : CATEGORY_VALUE.COLOR, r0, r1, r2) };
  if (r0 === r1 || r1 === r2) {
    const pair = r1;
    const kicker = r0 === r1 ? r2 : r0;
    return { category: 'PAIR', value: pack(CATEGORY_VALUE.PAIR, pair, kicker) };
  }
  return { category: 'HIGH_CARD', value: pack(CATEGORY_VALUE.HIGH_CARD, r0, r1, r2) };
}

/** >0 if a beats b, <0 if b beats a, 0 tie. `lowWins` implements the Muflis variant. */
export function compareTeenPatti(a: readonly CardId[], b: readonly CardId[], opts: TeenPattiRankOptions, lowWins = false): number {
  const d = evaluateTeenPatti(a, opts).value - evaluateTeenPatti(b, opts).value;
  return lowWins ? -d : d;
}

let percentileTable: { values: Float64Array; opts: string } | null = null;

/**
 * Fraction of all C(52,3)=22,100 hands that this hand beats (0..1). Used by bots.
 * Computed once (lazily) and cached.
 */
export function teenPattiPercentile(cards: readonly CardId[], opts: TeenPattiRankOptions = { ace23: 'second' }): number {
  const key = `${opts.ace23}${opts.colorFirst ? '+flush' : ''}${opts.brag ? '+brag' : ''}`;
  if (!percentileTable || percentileTable.opts !== key) {
    const suits = ['S', 'H', 'D', 'C'];
    const rankChars = '23456789TJQKA';
    const deck: string[] = [];
    for (const s of suits) for (const r of rankChars) deck.push(r + s);
    const vals: number[] = [];
    for (let i = 0; i < 52; i++)
      for (let j = i + 1; j < 52; j++)
        for (let k = j + 1; k < 52; k++) vals.push(evaluateTeenPatti([deck[i]!, deck[j]!, deck[k]!], opts).value);
    percentileTable = { values: Float64Array.from(vals.sort((a, b) => a - b)), opts: key };
  }
  const v = evaluateTeenPatti(cards, opts).value;
  const arr = percentileTable.values;
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid]! < v) lo = mid + 1;
    else hi = mid;
  }
  return lo / arr.length;
}
